/**
 * Message catalogue of the FWP backend protocol (version 1).
 *
 * Request `type` -> payload/result types, server event names -> payload types, and per-request metadata
 * used by the server router (authentication, idempotency caching). Runtime validators live in
 * ./validators.ts and are type-checked against these declarations.
 */
import type { JsonObject, JsonValue } from '../json';
import type { CommandLogData } from '../../session/command-log';
import type { InputCommand } from '../../session/input';
import type { EventEnvelope } from './envelope';
import type {
  AccountDeletedResult,
  AdContext,
  AdPlacement,
  AdProviderId,
  AdTicketInfo,
  AdsStatusResult,
  AssaultTarget,
  AssaultZone,
  AuctionInfo,
  AuctionState,
  AuthResult,
  AuthSessionInfo,
  BattleInit,
  BattleMode,
  BattleResult,
  ChatChannelInfo,
  ChatDeleteReason,
  ChatLimits,
  ChatMessage,
  ClanEvent,
  ClanInfo,
  ClanJoinPolicy,
  ClanJoinRequest,
  ClanMember,
  ClanMembershipReason,
  ClanMineResult,
  ClanRole,
  ClanSummary,
  CurrentSessionInfo,
  DeviceInfo,
  EmailStatusResult,
  EmailVerifiedResult,
  Grant,
  IntegrityChallenge,
  IntegrityProvider,
  IntegrityResult,
  IssuedAuthResult,
  LeaderboardBoard,
  LeaderboardPage,
  LegacyImport,
  MatchTicket,
  MessageLanguage,
  NewsItem,
  PlayerCard,
  PlayerCommandResult,
  PlayerId,
  PlayGamesStatusResult,
  PlayerProfile,
  ProfileSummary,
  PushDevicesResult,
  ProfileVolatile,
  PublicProfile,
  ReplayRecord,
  ReplaySummary,
  Room,
  RoomSettingsInput,
  SaveReason,
  SessionCheckpoint,
  SessionDisputeStatus,
  SessionFinishStatus,
  SessionInfo,
  SessionOutcome,
  SessionTurnSummary,
  SignedInResult,
  StoreCatalogResult,
  TeamSnapshotInput,
  Timestamp,
  TributeResult,
} from './model';

export type EmptyPayload = Record<string, never>;
export type EmptyResult = Record<string, never>;

export type FeatureFlag =
  | 'profile'
  | 'commands'
  | 'credentials'
  | 'legacyImport'
  | 'leaderboard'
  | 'assault'
  | 'clans'
  | 'chat'
  | 'auction'
  | 'news'
  | 'replays'
  | 'matchmaking'
  | 'rooms'
  | 'sessions'
  | 'eventsPoll'
  | 'store'
  | 'ads'
  | 'playGames'
  | 'email'
  | 'integrity'
  | 'push';

/** Every feature flag of this protocol version (system.hello `features` lists the enabled ones). */
export const ALL_FEATURES: readonly FeatureFlag[] = [
  'profile',
  'commands',
  'credentials',
  'legacyImport',
  'leaderboard',
  'assault',
  'clans',
  'chat',
  'auction',
  'news',
  'replays',
  'matchmaking',
  'rooms',
  'sessions',
  'eventsPoll',
  'store',
  'ads',
  'playGames',
  'email',
  'integrity',
  'push',
];

/**
 * Features an operator may switch off (server FEATURES variable); the requests of a disabled feature are refused
 * with FORBIDDEN { reason: 'feature_disabled', feature }. The core of the game (profile, commands, events) and the
 * legacy import (ALLOW_LEGACY_IMPORT) are not among them.
 */
export const OPTIONAL_FEATURES: readonly FeatureFlag[] = ['credentials', 'leaderboard', 'assault', 'clans', 'chat', 'auction', 'news', 'replays', 'matchmaking', 'rooms', 'sessions', 'store', 'ads', 'playGames', 'email', 'integrity', 'push'];

/**
 * Client platform of system.hello: 'web' | 'android' | 'ios' | 'desktop' | 'test' or another lower-case identifier
 * (DEVICE_PLATFORM_PATTERN).
 */
export type ClientPlatform = 'web' | 'android' | 'ios' | 'desktop' | 'test' | (string & {});

/**
 * List requests that take `{ limit?, cursor? }` and answer `{ items, next }`. The other lists are bounded by the
 * server: assault.getZones / assault.getTargets (one entry per zone), auth.sessions (MAX_SESSIONS_PER_ACCOUNT).
 */
export const PAGINATED_REQUESTS = [
  'leaderboard.get',
  'clan.members',
  'news.list',
  'replay.list',
  'room.list',
  'profile.listGrants',
  'clan.list',
  'clan.requests',
  'clan.history',
  'chat.history',
] as const;
export type PaginatedRequestType = (typeof PAGINATED_REQUESTS)[number];

/**
 * One page of a list: `next` is the opaque cursor of the following page (null = this is the last page); pass it
 * back as `cursor` with the same other parameters. A cursor this server did not issue for that list is BAD_REQUEST
 * 'bad_cursor'.
 */
export interface ListPage<T> {
  items: T[];
  next: string | null;
}

export interface ServerLimits {
  maxRequestBytes: number;
  maxGameStateBytes: number;
  /** Default page size of leaderboard.get; x leaderboardMaxPages = the entries a top shows (cursor form: any limit). */
  leaderboardPageSize: number;
  leaderboardMaxPages: number;
  maxInputBatch: number;
  /** Default and largest `limit` of each paginated list request. */
  pageSizes: { [K in PaginatedRequestType]: { default: number; max: number } };
  /** Request rate limit of one client (token, WebSocket connection or address); null = not limited. */
  rateLimit: { burst: number; perSecond: number; accountsPerIpBurst: number; accountsPerIpPerHour: number } | null;
  /** The server keeps the last `maxEvents` events of a player for `retentionMs` (events.resume / events.poll). */
  eventReplay: { maxEvents: number; retentionMs: number };
  /** Longest wait of one events.poll. */
  maxPollWaitMs: number;
  /** The rules of the player-made clans (src/shared/clan-rules.ts and the server's configuration). */
  clans: ClanLimits;
  /** The rules of the chat (src/shared/chat-rules.ts and the server's configuration). */
  chat: ChatLimits;
}

/** system.hello limits.clans: what a client checks before it sends a clan request. */
export interface ClanLimits {
  /** Clan name length after normalization (code points). */
  nameMin: number;
  nameMax: number;
  descriptionMax: number;
  maxMembers: number;
  maxOfficers: number;
  /** The emblems a clan may choose (frames of the game's clan_icons sprite). */
  emblems: number[];
  /** clan.create needs a registered account (not a guest) of at least this level. */
  minLevelToCreate: number;
  /** A player creates at most one clan per this period. */
  createCooldownMs: number;
  /** A leader renames the clan at most once per this period. */
  renameCooldownMs: number;
  /** After leaving or being kicked a player joins a clan again only after this period. */
  rejoinCooldownMs: number;
  /** Pending requests of one player / of one clan at once. */
  maxRequestsPerPlayer: number;
  maxRequestsPerClan: number;
}

export interface HelloPayload {
  /** The app's version, e.g. `1.4.2` or `fwp-android/1.4.2` (the first `major.minor[.patch]` counts). */
  clientVersion: string;
  /** Protocol versions the client speaks; the server picks the highest common one. */
  protocols: number[];
  platform?: ClientPlatform;
  /** Optional client capabilities (e.g. 'events.resume'), recorded by the server for this connection. */
  capabilities?: string[];
}

export interface HelloResult {
  protocol: number;
  serverVersion: string;
  /** Oldest client version this server accepts (older ones get VERSION_MISMATCH 'client_too_old'); null = any. */
  minClientVersion: string | null;
  /** Enabled feature flags. */
  features: FeatureFlag[];
  limits: ServerLimits;
  serverTime: Timestamp;
  /** The server's public base URL (links, other endpoints), when the operator configured one. */
  publicBaseUrl: string | null;
}

/** Events of a player after a position (`events.resume`, `events.poll`). */
export interface EventBatch {
  /** Events with seq > `after`, oldest first (empty when `gap`). */
  events: EventEnvelope[];
  /** The player's newest event seq: resume after it next time. */
  last: number;
  /**
   * True when events after `after` are no longer available (they fell out of the server's replay buffer, or the
   * server restarted): the client must reload its state (profile, rooms, sessions) and continue after `last`.
   */
  gap: boolean;
}

/** Machine-readable description of the protocol (`system.describe`, GET /api/schema); JSON Schema draft 2020-12. */
export interface ApiDescription {
  $schema: string;
  protocol: number;
  supportedProtocols: number[];
  serverVersion: string;
  /** The server's public base URL, when configured. */
  publicBaseUrl: string | null;
  /** HTTP paths (POST envelopes to `api`, WebSocket at `ws`). */
  paths: { api: string; versionedApi: string; schema: string; ws: string; health: string };
  /** JSON Schemas of the request, response and event envelopes. */
  envelopes: { request: Record<string, unknown>; response: Record<string, unknown>; event: Record<string, unknown> };
  requests: {
    [type: string]: {
      payload: Record<string, unknown>;
      /**
       * Changes state: a retry with the same request id is answered from the idempotency cache (auth.refresh, which is
       * no mutation here, is retried under the same id too: see its description).
       */
      mutation: boolean;
      /** Needs an authenticated session. */
      auth: boolean;
      /** Optional feature the request belongs to (refused while disabled). */
      feature?: FeatureFlag;
      /** Takes `limit` / `cursor` and answers `{ items, next }`. */
      paginated?: boolean;
      /** Kept for existing clients only; new clients use the replacement named in `description`. */
      deprecated?: boolean;
      description?: string;
      /** JSON Schema of the response's `result` (./contract.ts RESULT_SCHEMAS). */
      result: Record<string, unknown>;
    };
  };
  /** Server events with the JSON Schema of their `payload`. */
  events: { event: string; description: string; payload: Record<string, unknown> }[];
  errors: { code: string; description: string; retryable: boolean }[];
  /** Every stable `details.reason` (./reasons.ts): every error the server sends carries one of them. */
  errorReasons: string[];
  /** The reasons with their error codes, group and meaning (the other `details` fields they come with). */
  reasons: { reason: string; codes: string[]; group: string; description: string }[];
  /** The player.command ops: args, result and refusal reasons of each (./commands.ts). */
  commands: Record<string, { description: string; args: Record<string, unknown>; result: Record<string, unknown>; reasons: string[]; service?: true }>;
  /** Formats of the stored game state (`gameState`), by their `format` tag: schema, value markers, decoding rules. */
  stateFormats: Record<string, { description: string; schema: Record<string, unknown>; markers: Record<string, Record<string, unknown>>; decoding: string[] }>;
  features: { all: FeatureFlag[]; optional: FeatureFlag[]; enabled: FeatureFlag[] };
}

export interface RequestMap {
  // --- system
  /**
   * Capability/version negotiation (the first request of a client): the highest common protocol version, the server's
   * features and limits. No common version: VERSION_MISMATCH { reason: 'unsupported_protocol', supported }; a client
   * older than the server's minimum: VERSION_MISMATCH { reason: 'client_too_old', minClientVersion }.
   */
  'system.hello': { payload: HelloPayload; result: HelloResult };
  'system.time': { payload: EmptyPayload; result: { serverTime: Timestamp } };
  /** The machine-readable API description (JSON Schemas of every request payload); also GET /api/schema. */
  'system.describe': { payload: EmptyPayload; result: ApiDescription };
  /**
   * Event mailbox for transports without server push (HTTP). Long-polls up to `waitMs`. `after` is the seq of the
   * last event the client has (the same per-player seq as pushed events); without it (or 0) the mailbox starts at
   * this client's first request. `gap`: see EventBatch.
   */
  'events.poll': { payload: { after?: number; waitMs?: number }; result: EventBatch };
  /**
   * Missed events after a reconnect (WebSocket, in-process; HTTP: like a poll without waiting): the events after
   * `after` still in the server's replay buffer, or `gap: true` when some fell out of it. Without `after` it only
   * reports the current `last` (a client that has seen no event yet).
   */
  'events.resume': { payload: { after?: number }; result: EventBatch };

  // --- auth
  // Lifecycle (bearer credentials only, no cookies): system.hello -> auth.signUp / auth.signIn (the game's accounts:
  // nickname + password; also auth.guest, and auth.register / auth.login of the earlier username accounts) -> a new
  // device session: access + refresh token -> requests with `Authorization: Bearer <accessToken>` (HTTP) or on the
  // connection the auth request bound (WebSocket, in-process; after a reconnect `auth.resume` binds the new
  // connection) -> `auth.refresh` before or after the access token expires (UNAUTHORIZED 'token_expired'; the refresh
  // token is rotated) -> `auth.logout`. An app start restores the stored session: auth.refresh (or auth.resume with a
  // live access token), then auth.session. Every sign-in accepts `device` (shown by `auth.sessions`).
  /**
   * A new account with a nickname and a password (REST name: POST /auth/signup), signed in at once: the account, its
   * first device session and the player's canonical game state (profile.create) are created together. Nickname and
   * password rules: src/shared/auth-rules.ts; refusals: VALIDATION_FAILED { reason: 'nickname_*' | 'password_*',
   * field }, CONFLICT 'nickname_taken' (case-insensitive; also a guest default name).
   */
  'auth.signUp': { payload: { nickname: string; password: string; device?: DeviceInfo }; result: SignedInResult };
  /**
   * Sign-in with the nickname (any case, outer spaces ignored) and the password (REST name: POST /auth/signin). An
   * unknown nickname and a wrong password get the same UNAUTHORIZED 'invalid_credentials'; repeated failures for one
   * nickname are RATE_LIMITED 'too_many_attempts'.
   */
  'auth.signIn': { payload: { nickname: string; password: string; device?: DeviceInfo }; result: SignedInResult };
  /** The asking connection's device session and the player (REST name: GET /auth/session). */
  'auth.session': { payload: EmptyPayload; result: { session: CurrentSessionInfo; player: PlayerCard } };
  /** Guest sign-in: a known `deviceToken` -> its account; otherwise a new account and a new device token. */
  'auth.guest': { payload: { deviceToken?: string; displayName?: string; device?: DeviceInfo }; result: IssuedAuthResult };
  'auth.register': { payload: { username: string; password: string; displayName?: string; device?: DeviceInfo }; result: IssuedAuthResult };
  'auth.login': { payload: { username: string; password: string; device?: DeviceInfo }; result: IssuedAuthResult };
  /**
   * Binds this connection to an existing session by its access token (a reconnected WebSocket). An expired access
   * token is UNAUTHORIZED 'token_expired' (refresh first). A session token of the earlier protocol 1 backend is
   * accepted once and converted into a device session (the result then carries a refresh token).
   */
  'auth.resume': { payload: { token: string; device?: DeviceInfo }; result: AuthResult };
  /** Adds username/password credentials to the (guest) account; the session's tokens are rotated. */
  'auth.link': { payload: { username: string; password: string; device?: DeviceInfo }; result: IssuedAuthResult };
  /**
   * A new token pair for the refresh token's session (the refresh token is rotated: presenting an old one again revokes
   * the session). Binds this connection to the session. Needs no access token. A refresh whose reply was lost is
   * retried under the SAME request id with the same refresh token: within REFRESH_RETRY_WINDOW (default 2 minutes) of
   * the rotation, also after a server restart, it gets a valid pair (the lost one, or a new one that replaces it)
   * instead of counting as a reuse. The same token under another id is a reuse (after a 10 s grace window).
   */
  'auth.refresh': { payload: { refreshToken: string; device?: DeviceInfo }; result: IssuedAuthResult };
  /** The account's device sessions, most recently used first. */
  'auth.sessions': { payload: EmptyPayload; result: { sessions: AuthSessionInfo[] } };
  /** Ends one device session of the account (its WebSocket connections are closed with code 4001). */
  'auth.revoke': { payload: { sessionId: string }; result: { revoked: number } };
  /** Ends this session, or with `all` every session of the account. */
  'auth.logout': { payload: { all?: boolean }; result: { revoked: number } };
  /**
   * Sign-in with Google Play Games (feature 'playGames'): the server auth code of the Play Games SDK
   * (GamesSignInClient.requestServerSideAccess with the server's OAuth client id) -> the account linked to that Play
   * Games player (auth.linkPlayGames). Not linked: NOT_FOUND 'play_games_not_linked' (sign in as usual, then link).
   */
  'auth.playGames': { payload: { serverAuthCode: string; device?: DeviceInfo }; result: SignedInResult };
  /** Links the signed-in account to the Play Games player of the server auth code (optional; one account per player). */
  'auth.linkPlayGames': { payload: { serverAuthCode: string }; result: PlayGamesStatusResult };
  /** Removes the account's Play Games link. */
  'auth.unlinkPlayGames': { payload: EmptyPayload; result: PlayGamesStatusResult };
  /** Whether the server verifies Play Games sign-ins and whether this account is linked. */
  'auth.playGamesStatus': { payload: EmptyPayload; result: PlayGamesStatusResult };
  // E-mail sign-in (feature 'email': src/server/core/services/email.ts; address rules: src/shared/email-rules.ts). An
  // address becomes the account's sign-in address only once its link was opened (auth.emailVerify); until then it is
  // `pendingEmail` and blocks nobody. No answer says whether an address belongs to an account.
  /**
   * A new nickname account (the rules of auth.signUp) with a password, signed in at once, and a confirmation link to
   * `email` (`lang`: the language of the mail). The answer is the same whether or not the address belongs to another
   * account (that account's owner gets a notice instead of the link).
   */
  'auth.emailSignUp': { payload: { nickname: string; email: string; password: string; lang?: MessageLanguage; device?: DeviceInfo }; result: SignedInResult };
  /** Sign-in with the confirmed address and the password: UNAUTHORIZED 'invalid_credentials' for anything else. */
  'auth.emailSignIn': { payload: { email: string; password: string; device?: DeviceInfo }; result: SignedInResult };
  /** The account's sign-in address and the address waiting for its confirmation. */
  'auth.emailStatus': { payload: EmptyPayload; result: EmailStatusResult };
  /** Adds or changes the sign-in address (confirmed with the password): a confirmation link goes to the new address. */
  'auth.emailChange': { payload: { email: string; password: string; lang?: MessageLanguage }; result: EmailStatusResult };
  /** Sends the confirmation link of the pending address again. */
  'auth.emailResend': { payload: { lang?: MessageLanguage }; result: EmailStatusResult };
  /** Opens a confirmation link: its token (from the link's fragment) confirms the address. No session needed. */
  'auth.emailVerify': { payload: { token: string }; result: EmailVerifiedResult };
  /** Asks for a password reset link: always { accepted: true }, whether or not the address belongs to an account. */
  'auth.passwordResetRequest': { payload: { email: string; lang?: MessageLanguage }; result: { accepted: true } };
  /** Sets a new password with the token of a reset link; every session of the account ends. */
  'auth.passwordReset': { payload: { token: string; password: string }; result: { reset: true } };

  // --- account
  /**
   * Deletes the signed-in player's account for good (the self-service deletion mobile stores require). An account
   * that signs in with a password (a nickname or a username account) confirms with it (`password`; missing:
   * VALIDATION_FAILED 'password_required', wrong: UNAUTHORIZED 'invalid_credentials' as a failed auth.signIn, counted
   * against the same failure limits); a guest account deletes with its session alone. Every device session ends (other
   * WebSocket connections close with code 4001); the asking connection stays open without a session.
   */
  'account.delete': { payload: { password?: string }; result: AccountDeletedResult };

  // --- integrity (feature 'integrity': Google Play Integrity, src/server/core/services/integrity.ts)
  /** A one-time challenge (5 minutes) the app passes to the Play Integrity API as the request hash. */
  'integrity.challenge': { payload: EmptyPayload; result: IntegrityChallenge };
  /**
   * The integrity token of that challenge: the server decodes it at Google and records the verdict as a signal.
   * `provider` names the attestation service ('play' = Google Play Integrity, the default; a later iOS App Attest gets a
   * value of its own here without a new protocol version).
   */
  'integrity.verify': { payload: { token: string; challenge: string; provider?: IntegrityProvider }; result: IntegrityResult };

  // --- push notifications (feature 'push': Firebase Cloud Messaging, src/server/core/services/push.ts; opt-in)
  /** Registers this device's push token for the account (the player turned notifications on). */
  'push.register': { payload: { token: string; platform: 'android' | 'ios' | 'web'; lang?: MessageLanguage }; result: PushDevicesResult };
  /** Removes one push token (or, without `token`, every token of the account). */
  'push.unregister': { payload: { token?: string }; result: PushDevicesResult };
  /** Sends a test notification to the account's devices (at most one per minute). */
  'push.test': { payload: EmptyPayload; result: { sent: number } };

  // --- profile
  /**
   * Loads the player's profile. `tzOffsetMin` is the device's time zone (minutes east of UTC); the server keeps the
   * first one it gets and accepts a change at most once per 24 hours. At trust level L2 the server owns the state: it
   * creates it for a new account (`dev` asks for the developer test profile, honoured only where the backend allows
   * it), runs the session entry on every load and reports its facts in `volatile`.
   */
  'profile.load': {
    payload: { tzOffsetMin?: number; dev?: boolean };
    result: { profile: PlayerProfile; created: boolean; serverTime: Timestamp; pendingGrants: number; volatile?: ProfileVolatile };
  };
  'profile.save': {
    payload: {
      gameState: JsonValue;
      baseRevision: number;
      reason?: SaveReason;
      summary?: Partial<ProfileSummary>;
      team?: TeamSnapshotInput;
      consumedGrants?: string[];
    };
    result: { revision: number; updatedAt: Timestamp; serverTime: Timestamp; summary: ProfileSummary };
  };
  /** The signed-in player: nickname, level and experience from the canonical game state (REST name: GET /profile/me). */
  'profile.me': { payload: EmptyPayload; result: PlayerCard };
  'profile.setDisplayName': { payload: { displayName: string }; result: { displayName: string } };
  'profile.getPublic': { payload: { playerId: PlayerId }; result: { profile: PublicProfile } };
  'profile.getPublicBatch': { payload: { playerIds: PlayerId[] }; result: { profiles: PublicProfile[] } };
  'profile.importLegacy': { payload: { legacy: string; baseRevision: number }; result: { revision: number; legacyImport: LegacyImport; summary: ProfileSummary } };
  /** Pending grants, oldest first, paginated (`grants` = `items`, kept for older clients). */
  'profile.listGrants': { payload: { limit?: number; cursor?: string }; result: { grants: Grant[]; items: Grant[]; next: string | null } };

  // --- commands (the server owns the game state)
  /**
   * Applies one meta-game operation (`op` = a rule op id such as 'shop.buy', `args` its arguments) to the canonical
   * game state. `opId` identifies the logical command: a retry with the same opId (after a lost reply, even after a
   * server restart) gets the stored response and is never applied twice. Commands of a player are serialized; there
   * is no baseRevision. Precondition failures are VALIDATION_FAILED { reason, details }.
   */
  'player.command': { payload: { op: string; args: JsonObject; opId: string }; result: PlayerCommandResult };

  // --- leaderboards
  /**
   * A page of a top list. Paginated like every list (`limit` + the `next` cursor; `items` = `entries`), and also by
   * page number (`page`, `pageSize`) as the original tops (10 pages x 10). `cursor` excludes `page`, `limit`
   * excludes `pageSize`. The cursor form walks the same top whatever the `limit`: the first
   * limits.leaderboardPageSize x limits.leaderboardMaxPages entries (`totalEntries`).
   */
  'leaderboard.get': { payload: { board: LeaderboardBoard; page?: number; pageSize?: number; limit?: number; cursor?: string }; result: LeaderboardPage };

  // --- assault / clans
  'assault.getZones': { payload: EmptyPayload; result: { serverTime: Timestamp; zones: AssaultZone[] } };
  'assault.getTargets': { payload: { zone?: number }; result: { serverTime: Timestamp; targets: AssaultTarget[] } };
  'assault.report': {
    payload: { zone: number; outcome: 'win' | 'loss'; expectedStage: number; sessionId?: string; team?: TeamSnapshotInput };
    result: { zone: AssaultZone; captured: boolean; serverTime: Timestamp };
  };
  'assault.claimTribute': { payload: EmptyPayload; result: TributeResult };
  // Clans are made and run by players (src/shared/clan-rules.ts; limits.clans of system.hello): a leader, officers and
  // members; joining is open or by request. A clan holds the zones of its current members.
  /**
   * The clans, paginated: by rating (default; the highest first) or the newest first (`sort: 'new'`), optionally only
   * those whose name contains `query` (compared like clan names: case, lookalike letters, spaces and punctuation do not
   * matter). `clans` = `items` (the first page for older clients that send no cursor).
   */
  'clan.list': { payload: { sort?: 'rating' | 'new'; query?: string; limit?: number; cursor?: string }; result: { clans: ClanSummary[]; items: ClanSummary[]; next: string | null } };
  /** A clan's card: summary, description, leader, the zones its members hold. */
  'clan.get': { payload: { clanId: number }; result: { clan: ClanInfo } };
  /** The asking player's clan and role, its own pending requests, the clan's pending requests (leader and officers). */
  'clan.mine': { payload: EmptyPayload; result: ClanMineResult };
  /** Members of a clan: the leader, the officers, the members; each by captures, most first; paginated (`members` = `items`). */
  'clan.members': { payload: { clanId: number; limit?: number; cursor?: string }; result: { clanId: number; members: ClanMember[]; items: ClanMember[]; next: string | null } };
  /** A new clan with the asking player as its leader. */
  'clan.create': { payload: { name: string; emblem: number; joinPolicy?: ClanJoinPolicy; description?: string }; result: { clan: ClanInfo } };
  /** Changes the clan's name, emblem, join policy (leader) or description (leader, officers). */
  'clan.update': { payload: { clanId: number; name?: string; emblem?: number; joinPolicy?: ClanJoinPolicy; description?: string }; result: { clan: ClanInfo } };
  /** Joins an open clan. */
  'clan.join': { payload: { clanId: number }; result: { clanId: number; role: ClanRole } };
  /** Asks to join a clan whose policy is 'request'. */
  'clan.request': { payload: { clanId: number }; result: { request: ClanJoinRequest } };
  /** Withdraws the asking player's request. */
  'clan.cancelRequest': { payload: { clanId: number }; result: EmptyResult };
  /** The clan's pending requests, oldest first (leader and officers). */
  'clan.requests': { payload: { clanId: number; limit?: number; cursor?: string }; result: { items: ClanJoinRequest[]; next: string | null } };
  /** Accepts or declines a request (leader and officers). */
  'clan.decide': { payload: { clanId: number; playerId: PlayerId; accept: boolean }; result: { clan?: ClanInfo } };
  /** Removes a member (the leader anybody but itself, an officer only members). */
  'clan.kick': { payload: { clanId: number; playerId: PlayerId }; result: EmptyResult };
  /** Makes a member an officer or an officer a member (leader). */
  'clan.setRole': { payload: { clanId: number; playerId: PlayerId; role: 'officer' | 'member' }; result: EmptyResult };
  /** Hands the leadership to another member; the old leader becomes an officer (leader). */
  'clan.transfer': { payload: { clanId: number; playerId: PlayerId }; result: EmptyResult };
  /** Leaves the clan; the only member leaving disbands it. Without a clan: `{ clanId: null, previousClanId: null }`. */
  'clan.leave': { payload: EmptyPayload; result: { clanId: null; previousClanId: number | null; disbanded: boolean } };
  /** Disbands the clan; `confirmName` repeats its name (leader). */
  'clan.disband': { payload: { clanId: number; confirmName: string }; result: EmptyResult };
  /** The clan's history, newest first, paginated. */
  'clan.history': { payload: { clanId: number; limit?: number; cursor?: string }; result: { items: ClanEvent[]; next: string | null } };

  // --- chat (src/shared/chat-rules.ts; limits.chat of system.hello): the channel 'global' and the channel of the
  // player's clan 'clan:<clanId>'. A client subscribes to the channels it shows (chat.subscribe, e.g. while its chat
  // panel is open) and gets their new messages as chat.message events; chat.channels tells the unread counts.
  /**
   * The channels the asking player may read: the global one and its clan's, with the unread counts and whether it may
   * write. `exclude`: players the client hides (its own list, kept on the device): their messages are not counted.
   */
  'chat.channels': { payload: { exclude?: PlayerId[] }; result: { channels: ChatChannelInfo[]; mutedUntil: Timestamp | null } };
  /** A channel's messages, newest first, paginated (the channel keeps the last limits.chat.history* messages). */
  'chat.history': { payload: { channel: string; limit?: number; cursor?: string }; result: { channel: string; items: ChatMessage[]; next: string | null } };
  /** Writes a message to a channel; its subscribers get chat.message. */
  'chat.send': { payload: { channel: string; text: string }; result: { message: ChatMessage } };
  /** Adds channels to the player's subscriptions (their chat.message / chat.deleted events); answers all of them. */
  'chat.subscribe': { payload: { channels: string[] }; result: { subscribed: string[] } };
  /** Removes channels (without `channels`: every channel) from the player's subscriptions. */
  'chat.unsubscribe': { payload: { channels?: string[] }; result: { subscribed: string[] } };
  /** Moves the player's read position of a channel forward to `seq` (the newest message it showed). */
  'chat.markRead': { payload: { channel: string; seq: number }; result: { channel: string; readSeq: number; unread: number } };

  // --- auction (the original "king of the hill" for the Annihilator, paid in diamonds; see AuctionState)
  /**
   * The annihilator auction: the price of the next purchase (diamonds), the holder, since / endsAt, the winners of
   * earlier rounds and the asking player's Annihilators (`mine`). Buying is the player.command op 'auction.buy'
   * { expectedPrice } (the diamonds come from the canonical game state).
   */
  'auction.get': { payload: EmptyPayload; result: AuctionInfo };

  // --- store (in-app purchases; feature 'store')
  /**
   * The products the asking player may buy (the server's catalogue: product ids and what they credit; prices come
   * from the platform store), the providers the server verifies, and the obfuscated account id to pass with a purchase.
   * A purchase is credited with player.command 'store.redeem'.
   */
  'store.catalog': { payload: EmptyPayload; result: StoreCatalogResult };

  // --- ads (opt-in rewarded ads; feature 'ads')
  /** The placements, their daily caps and what the player has left today, and the ad providers of this server. */
  'ads.status': { payload: EmptyPayload; result: AdsStatusResult };
  /**
   * Opens a ticket for one rewarded ad the player chose to watch: pass `ticket.ssv` to the ad SDK. The server grants
   * the reward when the provider verifies the view (AdMob server-side verification); read the outcome with ads.ticket.
   */
  'ads.start': { payload: { placement: AdPlacement; provider: AdProviderId; context?: AdContext }; result: { ticket: AdTicketInfo } };
  /** One of the player's tickets (status, the granted reward). */
  'ads.ticket': { payload: { ticketId: string }; result: { ticket: AdTicketInfo } };
  /** Development provider only (servers with ADS_DEV_TEST): verifies a 'dev-test' ticket as the ad provider would. */
  'ads.testReward': { payload: { ticketId: string }; result: { ticket: AdTicketInfo } };

  // --- news
  /** Operator news, newest first, paginated. */
  'news.list': { payload: { limit?: number; cursor?: string }; result: { items: NewsItem[]; next: string | null } };

  // --- replays
  'replay.save': {
    payload: { mode: BattleMode; stage: number; result: BattleResult; init?: BattleInit; seed?: number; log?: CommandLogData; legacy?: string; battleId?: string };
    result: { replayId: string };
  };
  /**
   * A player's replays (default: the caller's), newest first, `limit` per page (default 20). `next` is the opaque
   * cursor of the following page (null = last page); pass it back as `cursor`. An unknown cursor is BAD_REQUEST
   * 'bad_cursor'.
   */
  'replay.list': { payload: { playerId?: PlayerId; limit?: number; cursor?: string }; result: { items: ReplaySummary[]; next: string | null } };
  'replay.get': { payload: { replayId: string }; result: { replay: ReplayRecord } };

  // --- matchmaking / rooms
  'matchmaking.join': { payload: { mode: 'just'; teams: number; wormsPerTeam: number }; result: { ticket: MatchTicket } };
  'matchmaking.leave': { payload: { ticketId: string }; result: EmptyResult };
  /** Public lobby rooms, oldest first, paginated (`rooms` = `items`, kept for older clients). */
  'room.list': { payload: { limit?: number; cursor?: string }; result: { rooms: Room[]; items: Room[]; next: string | null } };
  'room.get': { payload: { roomId: string }; result: { room: Room } };
  'room.create': { payload: { settings?: RoomSettingsInput; private?: boolean; maxPlayers?: number }; result: { room: Room } };
  'room.join': { payload: { roomId?: string; code?: string }; result: { room: Room } };
  'room.leave': { payload: { roomId: string }; result: EmptyResult };
  'room.update': { payload: { roomId: string; settings: RoomSettingsInput }; result: { room: Room } };
  'room.setReady': { payload: { roomId: string; ready: boolean }; result: { room: Room } };
  'room.setTeam': { payload: { roomId: string; team: number }; result: { room: Room } };
  'room.chat': { payload: { roomId: string; text: string }; result: EmptyResult };
  'room.start': { payload: { roomId: string }; result: { session: SessionInfo } };

  // --- sessions (battles)
  'session.start': { payload: { init: BattleInit }; result: { session: SessionInfo } };
  /**
   * Everything a client of any platform needs to rebuild a running battle: the session (init, seed, turn state,
   * version), the whole command log, its turns without the input, the state hashes all participants agreed on (a
   * rebuild compares its own checkpoints with them) and the tick of a state snapshot to start from (always null: a
   * rebuild re-simulates from tick 1 with the log).
   */
  'session.get': {
    payload: { sessionId: string };
    result: { session: SessionInfo; log: CommandLogData; turns: SessionTurnSummary[]; hashes: SessionCheckpoint[]; snapshotTick: number | null };
  };
  /**
   * Input of the current turn. `fromTick` must not leave a gap after the backend's confirmed tick; commands the
   * backend already has (a retried batch) are skipped, so re-sending an unacknowledged range is safe.
   */
  'session.input': {
    payload: { sessionId: string; turn: number; fromTick: number; throughTick: number; commands: InputCommand[] };
    result: { throughTick: number };
  };
  /**
   * Ends the current turn at `tick`, the tick in which the sender's simulation started the move of `nextTeam`. The
   * turn's owner sends it. After the server forced a turn (it closed the input after tick E, session.turn with a null
   * team) the participant that controls the team of the next move its simulation started reports it with
   * `forcedEnd: E`; the server accepts the first well-formed report (CONFLICT 'turn_forced' answers a plain end of a
   * forced turn).
   */
  'session.endTurn': {
    payload: { sessionId: string; turn: number; tick: number; nextTeam: number; forcedEnd?: number };
    result: { turn: number; team: number; startTick: number; deadlineAt: Timestamp | null };
  };
  'session.hash': { payload: { sessionId: string; tick: number; hash: string; algorithm?: string }; result: { match: boolean | null } };
  /**
   * Reports how the battle ended on this client (`result`, `finalHash`). An online session ends when every
   * participant reported (or the finish grace ran out): the server re-simulates the whole log, so the response of the
   * last report may be `status: 'verifying'` and the verdict arrives as session.ended.
   */
  'session.finish': {
    payload: { sessionId: string; result: BattleResult; finalHash?: string; log?: CommandLogData };
    result: { ended: boolean; outcome: SessionOutcome | null; status: SessionFinishStatus };
  };
  'session.forfeit': { payload: { sessionId: string }; result: EmptyResult };
  /**
   * An online battle whose simulation on this client disagrees with the battle (another participant's checkpoint hash
   * of `tick`, or a turn change its own simulation did not make): the server decides, never the client. It re-simulates
   * the log known so far and ends the session: the participants its simulation contradicts lose
   * (SessionOutcome.diverged), the only other participant (if one) wins, verdict 'rejected'; without a working battle
   * simulator nobody wins ('unverified').
   */
  'session.dispute': {
    payload: { sessionId: string; tick: number; reason?: string };
    result: { status: SessionDisputeStatus; outcome: SessionOutcome | null };
  };
}

export type RequestType = keyof RequestMap;
export type PayloadOf<T extends RequestType> = RequestMap[T]['payload'];
export type ResultOf<T extends RequestType> = RequestMap[T]['result'];

export interface EventMap {
  'profile.changed': { revision: number; updatedAt: Timestamp };
  'grant.received': { grant: Grant };
  'assault.zoneChanged': { zone: AssaultZone };
  'auction.updated': { auction: AuctionState };
  'matchmaking.matched': { ticketId: string; roomId: string };
  'room.updated': { room: Room };
  'room.closed': { roomId: string; reason: 'empty' | 'expired' | 'operator'; version: number };
  'room.chat': { roomId: string; playerId: PlayerId; displayName: string; text: string; at: Timestamp; version: number };
  'session.started': { session: SessionInfo };
  /** Relayed input: exactly the new commands for ticks `fromTick..throughTick` (receivers detect gaps). */
  'session.input': {
    sessionId: string;
    playerId: PlayerId;
    team: number;
    turn: number;
    fromTick: number;
    throughTick: number;
    commands: InputCommand[];
    /** The session's version after this change (SessionInfo.version). */
    version: number;
  };
  /**
   * A turn change: turn `endedTurn` ended in `endTick` and turn `turn` of `team` starts in `startTick`. A forced turn
   * comes in two steps: first `team` and `startTick` are null - the server closed the input of turn `endedTurn`
   * (= `turn`) after `endTick`, its later ticks are empty frames and `deadlineAt` is when the server stops waiting for
   * the report of its end -, then the reported change follows with `forced: true`.
   */
  'session.turn': {
    sessionId: string;
    endedTurn: number;
    endTick: number;
    turn: number;
    team: number | null;
    startTick: number | null;
    forced: boolean;
    deadlineAt: Timestamp | null;
    version: number;
  };
  'session.desync': { sessionId: string; tick: number; hashes: Record<PlayerId, string>; version: number };
  'session.ended': { sessionId: string; outcome: SessionOutcome; version: number };
  'clan.updated': { clan: ClanInfo };
  'clan.membership': { clanId: number | null; role: ClanRole | null; reason: ClanMembershipReason };
  'clan.requestsChanged': { clanId: number; pending: number };
  'chat.message': { message: ChatMessage };
  'chat.deleted': { channel: string; messageIds: string[]; reason: ChatDeleteReason };
}

export type EventType = keyof EventMap;
export type EventPayloadOf<E extends EventType> = EventMap[E];

/** What each server event means (published in the API description). */
export const EVENT_DESCRIPTIONS: { readonly [E in EventType]: string } = {
  'profile.changed': 'The stored profile changed through another connection of this player (reload it when idle).',
  'grant.received': 'An operator grant arrived for this player.',
  'assault.zoneChanged': 'An assault zone changed its owner or stage.',
  'auction.updated': 'The annihilator auction changed (a purchase, or a round ended with a permanent copy for its holder).',
  'matchmaking.matched': 'A matchmaking ticket found a room.',
  'room.updated': 'A room this player is in changed (players, readiness, settings).',
  'room.closed': 'A room this player was in was closed.',
  'room.chat': 'A chat message in a room this player is in.',
  'session.started': 'A battle session of this player started.',
  'session.input': 'Relayed battle input of another participant (ticks fromTick..throughTick).',
  'session.turn': 'The turn of a battle session changed, or (team null) the server closed the input of a forced turn.',
  'session.desync': 'Participants reported different state hashes for one tick.',
  'session.ended': 'A battle session ended with its outcome.',
  'clan.updated': "The player's clan changed (members, roles, leader, name, emblem, join policy, description): the new card.",
  'clan.membership': "The player's own clan or role changed (created, joined, accepted, left, kicked, disbanded, a new role, the leadership), or a request of the player ended without it joining (declined, the clan disbanded).",
  'clan.requestsChanged': 'Leader and officers: the number of pending requests of their clan changed.',
  'chat.message': 'A new message in a chat channel the player subscribed to (chat.subscribe).',
  'chat.deleted': 'Messages left a chat channel the player subscribed to: an operator removed them, or their sender deleted the account.',
};

export interface RequestMeta {
  /** Requires an authenticated player. */
  auth: boolean;
  /** Changes state: responses are cached by request id for idempotent retries. */
  mutating: boolean;
}

/** Requests kept only for existing clients (published with `deprecated: true`). */
export const DEPRECATED_REQUESTS: readonly RequestType[] = ['session.start'];

/** Router metadata for every request type (compile-time complete). */
export const REQUEST_META: { readonly [K in RequestType]: RequestMeta } = {
  'system.hello': { auth: false, mutating: false },
  'system.time': { auth: false, mutating: false },
  'system.describe': { auth: false, mutating: false },
  'events.poll': { auth: true, mutating: false },
  'events.resume': { auth: true, mutating: false },
  'auth.guest': { auth: false, mutating: true },
  // Sign-up and sign-in open a device session: a retry under the same id gets the same one (backend.ts idempotencyKey).
  'auth.signUp': { auth: false, mutating: true },
  'auth.signIn': { auth: false, mutating: true },
  'auth.session': { auth: true, mutating: false },
  'auth.register': { auth: false, mutating: true },
  // A sign-in opens a device session: a retry under the same id gets the same session (backend.ts idempotencyKey).
  'auth.login': { auth: false, mutating: true },
  'auth.resume': { auth: false, mutating: false },
  'auth.link': { auth: true, mutating: true },
  'auth.refresh': { auth: false, mutating: false },
  'auth.sessions': { auth: true, mutating: false },
  'auth.revoke': { auth: true, mutating: true },
  'auth.logout': { auth: true, mutating: true },
  // A Play Games sign-in opens a device session like auth.signIn (a retry under the same id gets the same session).
  'auth.playGames': { auth: false, mutating: true },
  'auth.linkPlayGames': { auth: true, mutating: true },
  'auth.unlinkPlayGames': { auth: true, mutating: true },
  'auth.playGamesStatus': { auth: true, mutating: false },
  // E-mail sign-up and sign-in open a device session like auth.signUp / auth.signIn.
  'auth.emailSignUp': { auth: false, mutating: true },
  'auth.emailSignIn': { auth: false, mutating: true },
  'auth.emailStatus': { auth: true, mutating: false },
  'auth.emailChange': { auth: true, mutating: true },
  'auth.emailResend': { auth: true, mutating: true },
  'auth.emailVerify': { auth: false, mutating: true },
  'auth.passwordResetRequest': { auth: false, mutating: true },
  'auth.passwordReset': { auth: false, mutating: true },
  'integrity.challenge': { auth: true, mutating: false },
  'integrity.verify': { auth: true, mutating: true },
  'push.register': { auth: true, mutating: true },
  'push.unregister': { auth: true, mutating: true },
  'push.test': { auth: true, mutating: true },
  'account.delete': { auth: true, mutating: true },
  'profile.load': { auth: true, mutating: false },
  'profile.save': { auth: true, mutating: true },
  'profile.me': { auth: true, mutating: false },
  'profile.setDisplayName': { auth: true, mutating: true },
  'profile.getPublic': { auth: true, mutating: false },
  'profile.getPublicBatch': { auth: true, mutating: false },
  'profile.importLegacy': { auth: true, mutating: true },
  'profile.listGrants': { auth: true, mutating: false },
  'player.command': { auth: true, mutating: true },
  'leaderboard.get': { auth: true, mutating: false },
  'assault.getZones': { auth: true, mutating: false },
  'assault.getTargets': { auth: true, mutating: false },
  'assault.report': { auth: true, mutating: true },
  'assault.claimTribute': { auth: true, mutating: true },
  'clan.list': { auth: true, mutating: false },
  'clan.get': { auth: true, mutating: false },
  'clan.mine': { auth: true, mutating: false },
  'clan.members': { auth: true, mutating: false },
  'clan.create': { auth: true, mutating: true },
  'clan.update': { auth: true, mutating: true },
  'clan.join': { auth: true, mutating: true },
  'clan.request': { auth: true, mutating: true },
  'clan.cancelRequest': { auth: true, mutating: true },
  'clan.requests': { auth: true, mutating: false },
  'clan.decide': { auth: true, mutating: true },
  'clan.kick': { auth: true, mutating: true },
  'clan.setRole': { auth: true, mutating: true },
  'clan.transfer': { auth: true, mutating: true },
  'clan.leave': { auth: true, mutating: true },
  'clan.disband': { auth: true, mutating: true },
  'clan.history': { auth: true, mutating: false },
  'chat.channels': { auth: true, mutating: false },
  'chat.history': { auth: true, mutating: false },
  'chat.send': { auth: true, mutating: true },
  // A subscription and a read position are set again by a retry (idempotent): no cached reply, which after a reconnect
  // would answer without subscribing the new connection's player again.
  'chat.subscribe': { auth: true, mutating: false },
  'chat.unsubscribe': { auth: true, mutating: false },
  'chat.markRead': { auth: true, mutating: false },
  'auction.get': { auth: true, mutating: false },
  'news.list': { auth: false, mutating: false },
  'store.catalog': { auth: true, mutating: false },
  'ads.status': { auth: true, mutating: false },
  'ads.start': { auth: true, mutating: true },
  'ads.ticket': { auth: true, mutating: false },
  'ads.testReward': { auth: true, mutating: true },
  'replay.save': { auth: true, mutating: true },
  'replay.list': { auth: true, mutating: false },
  'replay.get': { auth: true, mutating: false },
  'matchmaking.join': { auth: true, mutating: true },
  'matchmaking.leave': { auth: true, mutating: true },
  'room.list': { auth: true, mutating: false },
  'room.get': { auth: true, mutating: false },
  'room.create': { auth: true, mutating: true },
  'room.join': { auth: true, mutating: true },
  'room.leave': { auth: true, mutating: true },
  'room.update': { auth: true, mutating: true },
  'room.setReady': { auth: true, mutating: true },
  'room.setTeam': { auth: true, mutating: true },
  'room.chat': { auth: true, mutating: true },
  'room.start': { auth: true, mutating: true },
  'session.start': { auth: true, mutating: true },
  'session.get': { auth: true, mutating: false },
  'session.input': { auth: true, mutating: true },
  'session.endTurn': { auth: true, mutating: true },
  'session.hash': { auth: true, mutating: true },
  'session.finish': { auth: true, mutating: true },
  'session.forfeit': { auth: true, mutating: true },
  'session.dispute': { auth: true, mutating: true },
};

/** Notes on requests for clients of other platforms (published as `description` in the API description). */
export const REQUEST_DESCRIPTIONS: { readonly [K in RequestType]?: string } = {
  'auth.guest':
    "Guest sign-in. Without deviceToken: a new account and device token. A retry under the same request id (same payload, same client address) gets the same account and session. The deviceToken of an account that is being deleted (account.delete, an operator) gets NOT_FOUND 'account_not_found': the account is gone, forget the token.",
  'auth.signUp':
    "The game's sign-up (REST name: POST /auth/signup): a new account with a nickname (3..16 characters: Latin and Cyrillic letters, digits, _ - . and single inner spaces; case kept, unique case-insensitively) and a password (8..128 characters), its first device session and the player's game state, signed in at once. Refusals: VALIDATION_FAILED { reason, field } (nickname_required, nickname_too_short, nickname_too_long, nickname_invalid, password_required, password_too_short, password_too_long, password_invalid), CONFLICT 'nickname_taken'. A retry under the same request id with the same payload gets the same result.",
  'auth.signIn':
    "The game's sign-in (REST name: POST /auth/signin) with the nickname in any case. An unknown nickname and a wrong password both get UNAUTHORIZED 'invalid_credentials'; repeated failures for one nickname RATE_LIMITED 'too_many_attempts' (details.retryAfterMs). A retry under the same request id with the same payload gets the same session. Username accounts of auth.register sign in here with their username too. An account that is being deleted (account.delete, an operator) gets NOT_FOUND 'account_not_found': treat it as deleted.",
  'auth.emailSignUp':
    "A new nickname account with a password and an e-mail address, signed in at once (the rules and refusals of auth.signUp, plus VALIDATION_FAILED { reason: 'email_required' | 'email_too_long' | 'email_invalid', field: 'email' }). The address is only pending: a confirmation link goes to it (`lang`: uk, en or ru), and once it is opened (auth.emailVerify) the account signs in with it. The answer never says whether the address belongs to another account: that account's owner gets a notice instead of the link.",
  'auth.emailSignIn':
    "Sign-in with a confirmed e-mail address and the password. An unknown or unconfirmed address and a wrong password get the same UNAUTHORIZED 'invalid_credentials'; repeated failures RATE_LIMITED 'too_many_attempts' (details.retryAfterMs, scope).",
  'auth.emailChange':
    "Adds or changes the account's sign-in address, confirmed with the account's password (wrong: UNAUTHORIZED 'invalid_credentials', the session stays; an account without a password: FORBIDDEN 'password_not_set'). The new address is pending until its link is opened; the current one keeps working meanwhile and gets a notice. RATE_LIMITED 'email_send_limit' when this account asked for too many mails (details.retryAfterMs).",
  'auth.emailVerify':
    "Confirms an address with the token of its link (the part after #t= of https://<site>/link/?a=verify#t=<token>). Unknown, used or expired token: VALIDATION_FAILED 'email_token_invalid'; the address was confirmed by another account meanwhile: CONFLICT 'email_taken'. The answer shows the address partly hidden.",
  'auth.passwordResetRequest':
    "Sends a password reset link (valid 1 hour) to the address when it is the confirmed sign-in address of an account with a password. The answer is { accepted: true } in every case (no account enumeration); too many requests from this client: RATE_LIMITED 'email_send_limit'.",
  'auth.passwordReset':
    "Sets a new password (the rules of auth.signUp: VALIDATION_FAILED password_*) with the token of a reset link (the part after #t= of https://<site>/link/?a=reset#t=<token>); unknown, used or expired: VALIDATION_FAILED 'email_token_invalid'. Every device session of the account ends; sign in again with the new password.",
  'integrity.verify':
    "The Play Integrity token the app got for the challenge of integrity.challenge (StandardIntegrityTokenRequest requestHash = the challenge); provider: the attestation service of the token ('play' = Google Play Integrity, the only one now and the default; other platforms get their own value later). The server decodes it at Google (decodeIntegrityToken) and records the verdict for the player: a signal for purchases and sign-ins, never a refusal of its own. Unknown or expired challenge: VALIDATION_FAILED 'integrity_challenge_invalid'; Google unavailable: UNAVAILABLE 'integrity_unavailable'.",
  'push.register':
    "Registers a Firebase Cloud Messaging registration token of this device for the account (opt-in: the player turned notifications on in the game). At most 5 devices per account (the least recently registered goes); a token registered by another account moves to this one. The registration belongs to the device session of the request: when that session ends in any way (auth.logout, replaced by a sign-in elsewhere, revoked, a password reset, expired) the device is unregistered; the app registers again after its next sign-in.",
  'push.test': "Sends a test notification to every registered device of the account; at most one per minute (RATE_LIMITED 'push_test_limit').",
  'auth.session':
    'The device session of this connection (id, device, created / last seen, expiry of the access and the refresh token) and the player card (REST name: GET /auth/session). An app start restores its stored session with auth.refresh (or auth.resume), then asks this.',
  'account.delete':
    "Deletes the signed-in player's account and all of its data for good (self-service deletion). Accounts with a password confirm with it: missing VALIDATION_FAILED 'password_required', wrong UNAUTHORIZED 'invalid_credentials' (the answer of a failed auth.signIn; failures count against its RATE_LIMITED 'too_many_attempts'); a guest account deletes with its session. Every device session of the account ends and its other connections close (WebSocket code 4001). Records kept for other players (online battles) show the deleted player as playerId 'p_deleted'. A retry after a lost reply gets UNAUTHORIZED (the session no longer exists): the account is gone.",
  'profile.me': "The signed-in player's card: stable playerId, nickname, level and experience from the canonical game state, guest flag (REST name: GET /profile/me).",
  'auth.register':
    'A new account with username/password and its first device session (kept for existing clients; the game signs up with auth.signUp). A retry under the same request id with the same payload gets the same result.',
  'auth.login':
    "Opens a device session (kept for existing clients; the game signs in with auth.signIn). A retry under the same request id with the same payload gets the same session. An account that is being deleted (account.delete, an operator) gets NOT_FOUND 'account_not_found': treat it as deleted.",
  'auth.refresh':
    'Rotates the refresh token. Retry a refresh whose reply was lost under the SAME request id with the same refresh token: within the retry window (default 2 min), even after a server restart, it gets a valid pair; another id with a rotated token is a reuse and revokes the session.',
  'auction.get':
    'The annihilator auction ("king of the hill", paid in diamonds) and the asking player\'s Annihilators. Buy with player.command op \'auction.buy\' { expectedPrice }: VALIDATION_FAILED \'price_changed\' (details.price), \'already_holder\' or \'not_enough_diam\'.',
  'profile.listGrants': 'Pending grants, oldest first, paginated (`grants` = `items`, kept for older clients).',
  'room.start':
    'Host only, every player ready and connected, each on a team of its own (team_taken, team_missing). Starts the online battle: session.started carries SessionInfo with `online` (the battle init every client builds the battle from in the neutral context) and `seed`.',
  'session.start':
    'Deprecated (protocol 1 solo battles with a client-supplied BattleInit, kept for existing clients): solo battles run through player.command battle.start / battle.finish (server-built init, server seed, re-simulation), online battles through room.start.',
  'session.get':
    'Everything to rebuild a running battle: the session (init, seed, turn state, version), the whole command log, its turns, the checkpoint hashes all participants agreed on. State snapshots are not implemented: snapshotTick is reserved and always null, a rebuild re-simulates from tick 1 with the log.',
  'clan.list':
    "Clans, paginated (limit, cursor): sort 'rating' (default: the highest rating first) or 'new' (the newest first); `query` (2..20 characters) keeps the clans whose name contains it, compared like clan names (case, Latin / Cyrillic lookalikes, spaces and punctuation do not matter). `clans` = `items`: a request without a cursor gets the first page. A cursor of another sort is BAD_REQUEST 'bad_cursor'.",
  'clan.get': "A clan's card: summary (rating = the captures of the current members, zoneCount, emblem, joinPolicy), description, leader and the zones its members hold. Unknown: NOT_FOUND 'clan_not_found'.",
  'clan.mine':
    "The asking player's clan (null when none) and role, its own pending requests, the clan's pending requests (leader and officers; others 0), rejoinAt (the end of the rejoin cooldown after leaving or being kicked) and canCreate (whether clan.create would pass now; otherwise the refusal's reason and, for a cooldown, `at`).",
  'clan.members': 'Members of a clan: the leader, then the officers, then the members, each group by captures (most first); paginated (`members` = `items`). Unknown clan: NOT_FOUND \'clan_not_found\'.',
  'clan.create':
    "A new clan with the asking player as its leader (the name rules: limits.clans and src/shared/clan-rules.ts; joinPolicy 'request' by default). Refusals: FORBIDDEN 'guest_not_allowed' (a guest account), FORBIDDEN 'clan_level_too_low' (details.minLevel, level), RATE_LIMITED 'create_cooldown' (one clan per limits.clans.createCooldownMs; details.retryAfterMs), INVALID_STATE 'already_in_clan', VALIDATION_FAILED 'clan_name_required' / 'clan_name_too_short' / 'clan_name_too_long' / 'clan_name_invalid' / 'clan_name_denied' / 'emblem_invalid' / 'description_too_long' / 'description_invalid' / 'description_denied' (details.field), CONFLICT 'clan_name_taken'.",
  'clan.update':
    "Changes the name, emblem and join policy (leader) or the description (leader and officers; an empty one removes it). Only the given fields change; nothing to change answers the card as it is. Refusals: FORBIDDEN 'clan_rank_too_low', RATE_LIMITED 'rename_cooldown' (one rename per limits.clans.renameCooldownMs; details.retryAfterMs), the field refusals of clan.create, CONFLICT 'clan_name_taken'.",
  'clan.join':
    "Joins an open clan as a member; the player's pending requests end. Already in this clan: the same answer again. Refusals: FORBIDDEN 'clan_request_required' (the clan takes requests: clan.request), INVALID_STATE 'clan_full', INVALID_STATE 'already_in_clan' (details.clanId), RATE_LIMITED 'rejoin_cooldown' (details.retryAfterMs), NOT_FOUND 'clan_not_found'.",
  'clan.request':
    "Asks to join a clan whose policy is 'request'; the leader and officers get clan.requestsChanged. A request already pending is answered again. Refusals: INVALID_STATE 'clan_open' (join it with clan.join), 'already_in_clan', 'clan_full', 'request_limit' (limits.clans.maxRequestsPerPlayer), 'clan_requests_full' (limits.clans.maxRequestsPerClan), RATE_LIMITED 'rejoin_cooldown', NOT_FOUND 'clan_not_found'.",
  'clan.cancelRequest': 'Withdraws the asking player\'s request to this clan; no such request: the same empty answer.',
  'clan.requests': "The clan's pending requests, oldest first, paginated (leader and officers; others FORBIDDEN 'clan_rank_too_low').",
  'clan.decide':
    "Accepts (the player becomes a member; its other requests end) or declines (the request ends; declines are not in the history) a pending request (leader and officers). Refusals: NOT_FOUND 'request_not_found', INVALID_STATE 'clan_full' (the request stays), INVALID_STATE 'already_in_clan' (the player joined another clan meanwhile: the request is removed), FORBIDDEN 'clan_rank_too_low'.",
  'clan.kick':
    "Removes a member (the leader: anybody but itself; an officer: members only); the member's zones leave the clan with it. Refusals: NOT_FOUND 'not_a_member', VALIDATION_FAILED 'cannot_target_self', FORBIDDEN 'target_is_leader', FORBIDDEN 'clan_rank_too_low'.",
  'clan.setRole':
    "Makes a member an officer or an officer a member (leader; the same role again changes nothing). Refusals: INVALID_STATE 'officer_limit' (limits.clans.maxOfficers), NOT_FOUND 'not_a_member', VALIDATION_FAILED 'cannot_target_self', FORBIDDEN 'clan_rank_too_low'.",
  'clan.transfer':
    "Hands the leadership to another member (leader); the old leader becomes an officer (a member when the officer places are taken). Refusals: NOT_FOUND 'not_a_member', VALIDATION_FAILED 'cannot_target_self', FORBIDDEN 'clan_rank_too_low'.",
  'clan.leave':
    "Leaves the clan; the player's zones leave the clan with it, and it may join a clan again after limits.clans.rejoinCooldownMs. The leader hands the leadership over first (INVALID_STATE 'leader_must_transfer') unless it is the only member: then the clan is disbanded (disbanded: true). Without a clan: { clanId: null, previousClanId: null, disbanded: false }.",
  'clan.disband': "Disbands the clan (leader): every member leaves, the name becomes free, the history and the requests are deleted. `confirmName` repeats the clan's name (compared like clan names): otherwise VALIDATION_FAILED 'disband_confirm_mismatch'.",
  'clan.history':
    "The clan's history, newest first, paginated (at most the last 200 events are kept). Structured events (ClanEvent: type and fields; names as they are now): the client composes the texts. Unknown clan: NOT_FOUND 'clan_not_found'; a cursor of another clan: BAD_REQUEST 'bad_cursor'.",
  'chat.channels':
    "The channels the asking player may read: 'global' and, in a clan, 'clan:<clanId>' (with the clan's brief). Each with lastSeq, the player's readSeq, unread (messages after readSeq that others wrote; a channel the player sees for the first time starts read up to its newest message), canSend and sendBlock ('guest': guests read only; 'level': below limits.chat.minLevelToSend; 'muted': an operator muted the player until mutedUntil) and whether the player is subscribed to it. `exclude` (at most 200 player ids): the players this client hides (a list kept on the device; the server stores nothing of it): their messages are left out of `unread`.",
  'chat.history':
    "A channel's messages, newest first, paginated (limit, cursor); the channel keeps its last limits.chat.historyGlobal / historyClan messages. Messages are plain text (src/shared/chat-rules.ts: sanitized by the server, the operator's filter words masked with `*`): show them as text, never as markup. A clan channel of another clan (or after leaving the clan): FORBIDDEN 'chat_not_member'; a cursor of another channel: BAD_REQUEST 'bad_cursor'.",
  'chat.send':
    "Writes a message (the text sanitized by the shared rules, then 1..limits.chat.textMax characters; the filter words masked, never refused). The subscribers of the channel get chat.message (not this connection: the reply carries the message), and the sender's read position moves to it. Refusals: FORBIDDEN 'chat_not_member', FORBIDDEN 'guest_not_allowed' (guests read only), FORBIDDEN 'chat_level_too_low' (details.minLevel, level), FORBIDDEN 'chat_muted' (details.until), VALIDATION_FAILED 'chat_text_empty' / 'chat_text_too_long' (details.max), RATE_LIMITED 'chat_rate_limited' (limits.chat.burst per windowMs, minIntervalMs apart; details.retryAfterMs; with details.scope 'address': too many messages of all the players of one client address, CHAT_ADDRESS_BURST per windowMs), RATE_LIMITED 'chat_duplicate' (the same text within limits.chat.duplicateWindowMs; details.retryAfterMs).",
  'chat.subscribe':
    "Subscribes the player to channels (every connection of the player gets their chat.message and chat.deleted events) and answers every channel it is subscribed to. A subscription ends with chat.unsubscribe, when the player has been offline for a while (a client subscribes again after it reconnected), and for a clan channel when the player leaves the clan. A channel the player may not read: FORBIDDEN 'chat_not_member' (nothing is subscribed). Not cached by request id: a retry subscribes again.",
  'chat.unsubscribe': 'Ends the subscriptions to the given channels (without `channels`: to every channel) and answers the channels still subscribed.',
  'chat.markRead':
    "Moves the player's read position of a channel forward to `seq` (never back, never past the channel's newest message) and answers the unread count after it. FORBIDDEN 'chat_not_member' as chat.history.",
  'player.command':
    "Applies one meta-game op to the canonical game state (commands[op] of this description: args, result, refusal reasons). The reply carries the whole new gameState (stateFormats['fwp-state-v1']). Retry a lost reply with the same opId.",
  'session.input':
    'Input of the current turn by the controller of its team; contiguous batches (a gap is CONFLICT with details.throughTick). Online battles refuse surrender frames, unknown weapons and clicks outside the map (VALIDATION_FAILED with details.reason). A forced turn answers CONFLICT \'turn_forced\'.',
  'session.endTurn':
    'Ends the current turn in the tick in which the sender\'s simulation started the move of nextTeam. After the server forced a turn (session.turn with team null), the controller of the next move\'s team reports it with forcedEnd = the closed tick (anybody when that controller is gone or late); the first well-formed report wins.',
  'session.finish':
    'The result and final hash this participant\'s battle ended with. Online battles are re-simulated by the server once every participant reported: status \'verifying\', then session.ended with the verdict (the simulated winner is the result).',
  'session.hash':
    'The state hash of a move start (a checkpoint). Ticks from 1 up to a little beyond the confirmed input; different hashes of a confirmed tick are session.desync. A checkpoint of a forced turn\'s owner after its closed input is ignored (its simulation ran on dropped input).',
  'session.dispute':
    'This client\'s simulation disagrees with the online battle (session.desync it confirmed, or a turn change it did not simulate). Status \'resolving\': the server re-simulates the log and ends the session with session.ended; the participants its simulation contradicts are in outcome.diverged and lose.',
};

export const REQUEST_TYPES = Object.keys(REQUEST_META) as RequestType[];

export function isRequestType(t: unknown): t is RequestType {
  return typeof t === 'string' && Object.prototype.hasOwnProperty.call(REQUEST_META, t);
}

/**
 * The feature flag a request belongs to (requests without an entry belong to the always-on core). A request of a
 * feature the server has disabled is refused with FORBIDDEN { reason: 'feature_disabled', feature }.
 */
export const REQUEST_FEATURES: { readonly [K in RequestType]?: FeatureFlag } = {
  'auth.signUp': 'credentials',
  'auth.signIn': 'credentials',
  'auth.register': 'credentials',
  'auth.login': 'credentials',
  'auth.link': 'credentials',
  'profile.importLegacy': 'legacyImport',
  'leaderboard.get': 'leaderboard',
  'assault.getZones': 'assault',
  'assault.getTargets': 'assault',
  'assault.report': 'assault',
  'assault.claimTribute': 'assault',
  'clan.list': 'clans',
  'clan.get': 'clans',
  'clan.mine': 'clans',
  'clan.members': 'clans',
  'clan.create': 'clans',
  'clan.update': 'clans',
  'clan.join': 'clans',
  'clan.request': 'clans',
  'clan.cancelRequest': 'clans',
  'clan.requests': 'clans',
  'clan.decide': 'clans',
  'clan.kick': 'clans',
  'clan.setRole': 'clans',
  'clan.transfer': 'clans',
  'clan.leave': 'clans',
  'clan.disband': 'clans',
  'clan.history': 'clans',
  'chat.channels': 'chat',
  'chat.history': 'chat',
  'chat.send': 'chat',
  'chat.subscribe': 'chat',
  'chat.unsubscribe': 'chat',
  'chat.markRead': 'chat',
  'auction.get': 'auction',
  'news.list': 'news',
  'store.catalog': 'store',
  'ads.status': 'ads',
  'ads.start': 'ads',
  'ads.ticket': 'ads',
  'ads.testReward': 'ads',
  'auth.playGames': 'playGames',
  'auth.linkPlayGames': 'playGames',
  'auth.unlinkPlayGames': 'playGames',
  'auth.playGamesStatus': 'playGames',
  'auth.emailSignUp': 'email',
  'auth.emailSignIn': 'email',
  'auth.emailStatus': 'email',
  'auth.emailChange': 'email',
  'auth.emailResend': 'email',
  'auth.emailVerify': 'email',
  'auth.passwordResetRequest': 'email',
  'auth.passwordReset': 'email',
  'integrity.challenge': 'integrity',
  'integrity.verify': 'integrity',
  'push.register': 'push',
  'push.unregister': 'push',
  'push.test': 'push',
  'replay.save': 'replays',
  'replay.list': 'replays',
  'replay.get': 'replays',
  'matchmaking.join': 'matchmaking',
  'matchmaking.leave': 'matchmaking',
  'room.list': 'rooms',
  'room.get': 'rooms',
  'room.create': 'rooms',
  'room.join': 'rooms',
  'room.leave': 'rooms',
  'room.update': 'rooms',
  'room.setReady': 'rooms',
  'room.setTeam': 'rooms',
  'room.chat': 'rooms',
  'room.start': 'rooms',
  'session.start': 'sessions',
  'session.get': 'sessions',
  'session.input': 'sessions',
  'session.endTurn': 'sessions',
  'session.hash': 'sessions',
  'session.finish': 'sessions',
  'session.forfeit': 'sessions',
  'session.dispute': 'sessions',
  'events.poll': 'eventsPoll',
};

export function featureOf(type: RequestType): FeatureFlag | null {
  return REQUEST_FEATURES[type] ?? null;
}

export const EVENT_TYPES: readonly EventType[] = [
  'profile.changed',
  'grant.received',
  'assault.zoneChanged',
  'auction.updated',
  'matchmaking.matched',
  'room.updated',
  'room.closed',
  'room.chat',
  'session.started',
  'session.input',
  'session.turn',
  'session.desync',
  'session.ended',
  'clan.updated',
  'clan.membership',
  'clan.requestsChanged',
  'chat.message',
  'chat.deleted',
];
